import { useEffect, useRef, useState } from 'react'
import type { RewardItem } from '../../domain/reward'
import { AddIcon, CloseIcon, CoinsIcon, GiftIcon, StarIcon, TrashIcon } from './Icons'
import { useI18n } from '../../i18n'

interface Props {
  coins: number
  rewards: RewardItem[]
  targetRewardId: string | null
  open: boolean
  saving: boolean
  error: string | null
  onClose: () => void
  onCreate: (name: string, cost: number) => Promise<boolean>
  onRedeem: (reward: RewardItem) => Promise<boolean>
  onDelete: (reward: RewardItem) => Promise<boolean>
  onTarget: (id: string | null) => Promise<boolean>
}

export const RewardsSheet = ({ coins, rewards, targetRewardId, open, saving, error, onClose, onCreate, onRedeem, onDelete, onTarget }: Props) => {
  const { copy: { common, rewards: rewardsCopy } } = useI18n()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const confirmRef = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState('')
  const [costText, setCostText] = useState('')
  const [pendingRedeem, setPendingRedeem] = useState<RewardItem | null>(null)
  const cost = Number.parseInt(costText, 10)
  const validCost = Number.isSafeInteger(cost) && cost >= 1 && cost <= 999_999_999

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  useEffect(() => {
    if (open) {
      setName('')
      setCostText('')
      setPendingRedeem(null)
    }
  }, [open])

  useEffect(() => {
    const dialog = confirmRef.current
    if (!dialog) return
    if (pendingRedeem && !dialog.open) dialog.showModal()
    if (!pendingRedeem && dialog.open) dialog.close()
  }, [pendingRedeem])

  const create = async () => {
    if (!name.trim() || !validCost) return
    if (await onCreate(name, cost)) {
      setName('')
      setCostText('')
    }
  }

  const redeem = async () => {
    if (!pendingRedeem) return
    if (await onRedeem(pendingRedeem)) setPendingRedeem(null)
  }

  return <>
    <dialog ref={dialogRef} className="sheet rewards-sheet" onCancel={(event) => { event.preventDefault(); onClose() }} onClose={onClose}>
      <div className="sheet__body rewards-sheet__body">
        <div className="sheet__handle" />
        <div className="sheet__heading"><div><h2>{rewardsCopy.title}</h2><p>{rewardsCopy.intro}</p></div><button className="icon-button" type="button" onClick={onClose} aria-label={common.close}><CloseIcon /></button></div>

        <div className="reward-balance"><CoinsIcon /><div><span className="eyebrow">{rewardsCopy.balance}</span><strong>{coins} {common.coins}</strong></div></div>

        <p className="eyebrow reward-section-title">{rewardsCopy.new}</p>
        <label className="field reward-field"><span>{common.name}</span><input value={name} onChange={(event) => setName(event.target.value.slice(0, 60))} placeholder={rewardsCopy.example} maxLength={60} /></label>
        <label className="field reward-field"><span>{rewardsCopy.cost}</span><input value={costText} onChange={(event) => setCostText(event.target.value.replace(/\D/g, '').slice(0, 9))} inputMode="numeric" pattern="[0-9]*" /></label>
        <button className="button button--primary reward-create" type="button" disabled={saving || !name.trim() || !validCost} onClick={() => void create()}><AddIcon />{rewardsCopy.add}</button>

        {error && <div className="error-message reward-error" role="alert">{error}</div>}
        <p className="eyebrow reward-section-title">{rewardsCopy.store} ({rewards.length})</p>
        {rewards.length === 0 ? <div className="rewards-empty">{rewardsCopy.empty}</div> : <div className="reward-list">{rewards.map((reward) => {
          const isTarget = targetRewardId === reward.id
          return <article className={isTarget ? 'reward-row reward-row--target' : 'reward-row'} key={reward.id}><span className="reward-row__icon"><GiftIcon /></span><div className="reward-row__content"><strong>{reward.name}</strong><small>{reward.cost} 🪙{reward.redeemedCount > 0 ? ` · ${rewardsCopy.redeemed} ${reward.redeemedCount} ${reward.redeemedCount === 1 ? rewardsCopy.time : rewardsCopy.times}` : ''}</small><button className={isTarget ? 'reward-target reward-target--selected' : 'reward-target'} type="button" aria-pressed={isTarget} disabled={saving} onClick={() => void onTarget(isTarget ? null : reward.id)}><StarIcon />{isTarget ? rewardsCopy.currentGoal : rewardsCopy.markGoal}</button></div><button className="button reward-redeem" type="button" disabled={saving || coins < reward.cost} onClick={() => setPendingRedeem(reward)}>{rewardsCopy.redeem}</button><button className="icon-button reward-delete" type="button" disabled={saving} onClick={() => void onDelete(reward)} aria-label={`${rewardsCopy.delete} ${reward.name}`}><TrashIcon /></button></article>
        })}</div>}
        <p className="reward-footer">{rewardsCopy.footer}</p>
      </div>
    </dialog>

    <dialog ref={confirmRef} className="confirm-dialog" onCancel={(event) => { event.preventDefault(); setPendingRedeem(null) }} onClose={() => setPendingRedeem(null)}>
      {pendingRedeem && <div className="redeem-confirm"><GiftIcon /><h2>{rewardsCopy.redeem} {pendingRedeem.name}</h2><p>{pendingRedeem.cost} {rewardsCopy.deduct}</p><div><button className="button button--ghost" type="button" disabled={saving} onClick={() => setPendingRedeem(null)}>{common.cancel}</button><button className="button reward-redeem" type="button" disabled={saving || coins < pendingRedeem.cost} onClick={() => void redeem()}>{rewardsCopy.redeem}</button></div></div>}
    </dialog>
  </>
}
